import { AnimatePresence, motion } from 'framer-motion';
import {
  LuBookOpen,
  LuChevronDown,
  LuLock,
  LuRotateCw,
  LuSignpost,
  LuStar,
  LuTriangleAlert,
} from 'react-icons/lu';
import { ProgressBar } from '../../design-system/components';
import { formatPercent } from '../../utils/percent';
import { LessonCard } from './LessonCard';
import { nextLessonId } from './lessonMarker';
import type { ExtendedLesson, PathWithExtendedInfo } from './types';

interface PathAccordionProps {
  path: PathWithExtendedInfo;
  isExpanded: boolean;
  onToggle: () => void;
  lessons: ExtendedLesson[];
  isLoadingLessons: boolean;
  hasError?: boolean;
  onRetry?: () => void;
}

/**
 * A chapter (path): its header card, then its lessons on the dotted rail
 * (redesign PR4, course.css). Open: the warm card with the orange border;
 * mastered: gold; locked: muted and disabled. Same toggle and states.
 */
export function PathAccordion({
  path,
  isExpanded,
  onToggle,
  lessons,
  isLoadingLessons,
  hasError = false,
  onRetry,
}: PathAccordionProps) {
  const state = path.locked
    ? 'locked'
    : isExpanded
      ? 'expanded'
      : path.mastered
        ? 'mastered'
        : 'collapsed';
  const nextId = nextLessonId(lessons);

  return (
    <div data-path-id={path.id} className="ee-chapter">
      {/* Path Header - Clickable */}
      <button
        type="button"
        onClick={() => !path.locked && onToggle()}
        disabled={path.locked}
        className={`ee-chapter__head ee-chapter__head--${state}`}
      >
        <span className="ee-chapter__row">
          {/* Path Icon */}
          <span
            className={`ee-course-tile ee-chapter__icon ${path.mastered && !path.locked ? 'ee-course-tile--gold' : ''}`}
          >
            {path.locked ? (
              <LuLock aria-hidden="true" />
            ) : path.mastered ? (
              <>
                <img
                  src="/giraffe_graduated.webp"
                  alt="Path mastered"
                  className="ee-chapter__mastered"
                />
                <span className="ee-lesson__star" aria-hidden="true">
                  <LuStar />
                </span>
              </>
            ) : (
              <LuSignpost aria-hidden="true" />
            )}
          </span>

          {/* Path Info */}
          <span className="ee-chapter__info">
            <h3 className="ee-chapter__title">{path.title}</h3>
            {path.description ? <p className="ee-chapter__desc">{path.description}</p> : null}
          </span>

          {/* Expand Icon */}
          {!path.locked && (
            <motion.span
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.2 }}
              className="ee-course-toggle"
            >
              <LuChevronDown aria-hidden="true" />
            </motion.span>
          )}
        </span>

        {/* Progress Bars - Compact */}
        {!path.locked && !isExpanded && (
          <span className="ee-chapter__bars">
            {(path.average_score ?? 0) > 0 && (
              <span className="ee-chapter__bar">
                <span className="ee-chapter__bar-label">
                  <span>Score</span>
                  <span className="ee-chapter__bar-value">{formatPercent(path.average_score)}</span>
                </span>
                <ProgressBar value={path.average_score ?? 0} tone="teal" size="thin" />
              </span>
            )}
            <span className="ee-chapter__bar">
              <span className="ee-chapter__bar-label">
                <span>Progress</span>
                <span className="ee-chapter__bar-value">{formatPercent(path.progress)}</span>
              </span>
              <ProgressBar value={path.progress || 0} size="thin" />
            </span>
          </span>
        )}
      </button>

      {/* Lessons List - Expandable */}
      <AnimatePresence>
        {isExpanded && !path.locked ? (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="ee-chapter__lessons"
          >
            {isLoadingLessons ? (
              <div className="ee-chapter__note" role="status" aria-label="Loading lessons">
                <div className="loading loading-dots loading-sm ee-course-loading"></div>
              </div>
            ) : hasError ? (
              <div className="ee-chapter__note">
                <LuTriangleAlert
                  className="ee-chapter__note-icon ee-chapter__note-icon--error"
                  aria-hidden="true"
                />
                <p>Failed to load lessons</p>
                {onRetry ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRetry();
                    }}
                    className="ee-chapter__retry"
                  >
                    <LuRotateCw aria-hidden="true" />
                    Retry
                  </button>
                ) : null}
              </div>
            ) : lessons.length > 0 ? (
              <motion.div
                className="ee-lesson-rail"
                initial="hidden"
                animate="visible"
                variants={{
                  visible: {
                    transition: { staggerChildren: 0.05 },
                  },
                }}
              >
                {(() => {
                  // Find the last lesson to mark as "last" for scroll fallback
                  const lastLessonId = lessons[lessons.length - 1]?.id;

                  return lessons.map((lesson) => (
                    <LessonCard
                      key={lesson.id}
                      lesson={lesson}
                      isCurrent={lesson.id === lastLessonId}
                      isNext={lesson.id === nextId}
                    />
                  ));
                })()}
              </motion.div>
            ) : (
              <div className="ee-chapter__note">
                <LuBookOpen className="ee-chapter__note-icon" aria-hidden="true" />
                No lessons available
              </div>
            )}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
